"use client";

import React from "react";
import Image from "next/image";

interface RelationshipItem {
  id: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
}

interface RelationshipsSectionProps {
  title?: string;
  subtitle?: string;
  relationships?: RelationshipItem[];
}

const defaultRelationships: RelationshipItem[] = [
  {
    id: "digital-transformation",
    title: "Sử dụng công nghệ để giảm phát thải",
    description:
      "Công nghệ số như trí tuệ nhân tạo (AI), Internet of Things (IoT) và dữ liệu lớn (Big Data) giúp theo dõi và phân tích hiệu quả năng lượng trong các ngành công nghiệp, từ đó tối ưu hóa việc sử dụng tài nguyên và giảm lượng phát thải khí nhà kính. Ví dụ, IoT có thể giúp giám sát và điều chỉnh mức tiêu thụ năng lượng của các thiết bị trong thời gian thực.",
    image: "/tuvanxanh.png", // Placeholder - replace with actual image
    imageAlt: "Digital technology for emission reduction",
  },
  {
    id: "process-optimization",
    title: "Tối ưu hóa quy trình sản xuất và tiêu thụ",
    description:
      "Chuyển đổi số hỗ trợ doanh nghiệp chuyển sang mô hình sản xuất thông minh, tự động hóa quy trình để giảm thiểu lãng phí và tiết kiệm năng lượng. Các hệ thống thông minh có thể giúp doanh nghiệp giảm thiểu lượng khi thải và tối ưu hóa việc vận hành.",
    image: "/tuvanxanh.png", // Placeholder - replace with actual image
    imageAlt: "Process optimization for production and consumption",
  },
  {
    id: "transparency-connectivity",
    title: "Tăng cường sự minh bạch và kết nối",
    description:
      "Chuyển đổi số cung cấp nền tảng để doanh nghiệp có thể minh bạch hơn trong các hoạt động bảo vệ môi trường, giúp để dễ dàng báo cáo và giám sát các chỉ tiêu bền vững. Đồng thời, công nghệ số giúp kết nối các bên liên quan như nhà cung cấp, khách hàng và chính phủ trong việc thúc đẩy các chiến lược bảo vệ môi trường.",
    image: "/tuvanxanh.png", // Placeholder - replace with actual image
    imageAlt: "Transparency and connectivity in environmental protection",
  },
];

/**
 * RelationshipsSection Component
 * Displays the digital transformation relationships section
 */
const RelationshipsSection: React.FC<RelationshipsSectionProps> = ({
  title = "Chuyển Đổi Số trong Chuyển Đổi Xanh: Mối Quan Hệ Giữa Công Nghệ và Bền Vững",
  subtitle = "Chuyển đổi số và chuyển đổi xanh là hai xu hướng phát triển không thể tách rời trong kỷ nguyên hiện đại, đặc biệt khi công nghệ số đóng vai trò quan trọng trong việc thúc đẩy bền vững môi trường. Chuyển đổi số trong chuyển đổi xanh không chỉ là việc áp dụng công nghệ số để tối ưu hóa quy trình sản xuất, mà còn là cách thức các doanh nghiệp và tổ chức có thể giảm thiểu tác động môi trường thông qua việc sử dụng dữ liệu và công nghệ thông minh.",
  relationships = defaultRelationships,
}) => {
  return (
    <section className="py-16 lg:py-24 bg-gray-50">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            {title}
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Relationships Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-8 lg:gap-12">
          {relationships.map((item, index) => (
            <div
              key={item.id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col"
            >
              {/* Image Section */}
              <div className="relative h-64">
                <Image
                  src={item.image}
                  alt={item.imageAlt}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                />

                {/* Decorative overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>

              {/* Content Section */}
              <div className="p-6 lg:p-8 flex flex-col flex-1">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4">
                  {item.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {item.description}
                </p>

                {/* Index Badge */}
                <div className="mt-auto pt-6 flex items-center">
                  <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <div className="ml-3 text-sm text-gray-500 font-medium">
                    Khía cạnh {index + 1}/3
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelationshipsSection;
